/**
 * AnomaliesList — liste des anomalies d'une submission.
 *
 *  - Récupère via `useAnomaliesBySubmission(submissionId)`
 *  - Tri canonique : non-résolues d'abord (critical → high → warn → info),
 *    résolues en bas grisées.
 *  - Toggle "Afficher aussi les résolues" (par défaut OFF).
 *  - Action "Résoudre" inline (ouvre `AnomalyResolver`).
 *  - Action "Rouvrir" pour anomalie résolue (PATCH /reopen direct, sans form).
 *  - Bandeau d'info léger : "Les anomalies ouvertes peuvent affecter les
 *    prochaines étapes d'analyse/comparaison." (texte UI uniquement,
 *    pas de gating technique 9.D).
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  sortAnomaliesCanonical,
  useAnomaliesBySubmission,
  useReopenAnomaly,
  type AnomalyRow,
  type AnomalySeverity,
} from "../../../lib/queries/anomalies";
import { toast } from "../../../lib/toast";

import { AnomalyResolver } from "./AnomalyResolver";

export type AnomaliesListProps = {
  submissionId: string;
};

export function AnomaliesList(props: AnomaliesListProps) {
  const { submissionId } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const [showResolved, setShowResolved] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const list = useAnomaliesBySubmission(submissionId);
  const reopen = useReopenAnomaly();

  const all = list.data?.items ?? [];
  const sorted = useMemo(() => sortAnomaliesCanonical(all), [all]);
  const visible = useMemo(
    () =>
      showResolved
        ? sorted
        : sorted.filter((a) => a.resolved_at == null),
    [sorted, showResolved],
  );

  const openCount = sorted.filter((a) => a.resolved_at == null).length;
  const resolvedCount = sorted.length - openCount;

  const onReopen = async (a: AnomalyRow) => {
    try {
      await reopen.mutateAsync({ anomalyId: a.id, submissionId });
      toast.success(t("submissions.detail.anomalies.reopen.success"));
    } catch (e) {
      const message =
        e instanceof ApiException
          ? getApiErrorMessage(e, lang) || t("errors.generic")
          : e instanceof Error
            ? e.message
            : t("errors.generic");
      toast.error(message);
    }
  };

  if (list.isLoading) {
    return (
      <p className="text-sm text-slate-500">
        {t("submissions.detail.anomalies.loading")}
      </p>
    );
  }

  if (list.error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
        {list.error instanceof ApiException
          ? getApiErrorMessage(list.error, lang) || t("errors.generic")
          : t("errors.generic")}
      </p>
    );
  }

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        {t("submissions.detail.anomalies.empty")}
      </p>
    );
  }

  return (
    <div>
      <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
        {t("submissions.detail.anomalies.warning_downstream")}
      </p>

      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-600">
          {t("submissions.detail.anomalies.count", {
            open: openCount,
            resolved: resolvedCount,
          })}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            className="h-4 w-4"
          />
          {t("submissions.detail.anomalies.show_resolved")}
        </label>
      </div>

      <ul className="space-y-2">
        {visible.map((a) => {
          const isResolved = a.resolved_at != null;
          return (
            <li
              key={a.id}
              className={`rounded-md border p-3 text-sm ${
                isResolved
                  ? "border-slate-200 bg-slate-50 text-slate-500 opacity-70"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex flex-wrap items-start gap-2">
                <SeverityBadge severity={a.severity} resolved={isResolved} />
                <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-700">
                  {a.type}
                </span>
                {isResolved && (
                  <span className="rounded bg-green-100 px-2 py-0.5 text-[11px] text-green-800">
                    {t("submissions.detail.anomalies.status.resolved")}
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p className={isResolved ? "line-through" : "font-medium"}>
                    {a.message}
                  </p>
                  {a.field_keys && a.field_keys.length > 0 && (
                    <p className="mt-1 text-xs text-slate-500">
                      {t("submissions.detail.anomalies.field_keys")}:{" "}
                      <span className="font-mono">
                        {a.field_keys.join(", ")}
                      </span>
                    </p>
                  )}
                  {isResolved && a.resolution_note && (
                    <p className="mt-1 text-xs italic text-slate-500">
                      « {a.resolution_note} »
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-2 flex items-center gap-3 text-xs">
                {!isResolved && resolvingId !== a.id && (
                  <button
                    type="button"
                    onClick={() => setResolvingId(a.id)}
                    className="text-brand-700 hover:underline"
                  >
                    {t("submissions.detail.anomalies.resolve.open")}
                  </button>
                )}
                {isResolved && (
                  <button
                    type="button"
                    onClick={() => onReopen(a)}
                    disabled={reopen.isPending}
                    className="text-slate-600 hover:text-slate-900 hover:underline"
                  >
                    {t("submissions.detail.anomalies.reopen.action")}
                  </button>
                )}
              </div>

              {resolvingId === a.id && !isResolved && (
                <AnomalyResolver
                  anomalyId={a.id}
                  submissionId={submissionId}
                  onClose={() => setResolvingId(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SeverityBadge
// -----------------------------------------------------------------------------

function SeverityBadge({
  severity,
  resolved,
}: {
  severity: AnomalySeverity;
  resolved: boolean;
}) {
  const { t } = useTranslation();
  const tone = resolved
    ? "border-slate-300 bg-slate-100 text-slate-500"
    : severity === "critical"
      ? "border-red-300 bg-red-100 text-red-800"
      : severity === "high"
        ? "border-orange-300 bg-orange-100 text-orange-800"
        : severity === "warn"
          ? "border-amber-300 bg-amber-100 text-amber-800"
          : "border-blue-300 bg-blue-100 text-blue-800";
  return (
    <span
      className={`rounded border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${tone}`}
    >
      {t(`submissions.detail.anomalies.severity.${severity}`)}
    </span>
  );
}
